import React, {
  InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react"
import { IconBaseProps } from "react-icons/lib"
import { FiAlertCircle } from "react-icons/fi"
import { useField } from "@unform/core"

import { Container, Error, Suggestions } from "./style"

interface Suggestion {
  id: string
  description: string
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  name: string
  containerStyle?: object
  icon?: React.ComponentType<IconBaseProps>
  suggestions: Suggestion[]
  onSelectSuggestion?: (value: string) => void
  defaultValueId?: string | null
}

const Autocomplete: React.FC<InputProps> = ({
  name,
  containerStyle = {},
  icon: Icon,
  suggestions,
  onSelectSuggestion,
  defaultValueId,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState("")
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isFilled, setIsFilled] = useState(false)
  const [filteredSuggestions, setFilteredSuggestions] = useState<Suggestion[]>(
    []
  )
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const { fieldName, error, registerField } = useField(name)

  const handleInputFocus = useCallback(() => {
    setIsFocused(true)
    setFilteredSuggestions(suggestions)
    setShowSuggestions(true)
  }, [suggestions])

  const handleInputBlur = useCallback(() => {
    setIsFocused(false)
    setIsFilled(!!inputRef.current?.value)

    setTimeout(() => {
      setShowSuggestions(false)
    }, 200)
  }, [])

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setInputValue(value)
      setSelectedId(null)

      const filtered = value
        ? suggestions.filter((item) =>
            item.description.toLowerCase().includes(value.toLowerCase())
          )
        : suggestions

      setFilteredSuggestions(filtered)
      setShowSuggestions(true)
      setHighlightedIndex(-1)
    },
    [suggestions]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlightedIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      )
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      )
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (highlightedIndex >= 0) {
        const selected = filteredSuggestions[highlightedIndex]
        selectSuggestion(selected)
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false)
    }
  }

  const selectSuggestion = (suggestion: Suggestion) => {
    setInputValue(suggestion.description)
    setSelectedId(suggestion.id)
    setShowSuggestions(false)
    setHighlightedIndex(-1)

    if (onSelectSuggestion) onSelectSuggestion(suggestion.id)
  }

  useEffect(() => {
    if (defaultValueId) {
      const selected = suggestions.find((item) => item.id === defaultValueId)
      if (selected) {
        setSelectedId(selected.id)
        setInputValue(selected.description)
      }
    }
  }, [defaultValueId, suggestions])

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: "value",
      getValue: () => selectedId,
      clearValue: () => {
        setSelectedId("")
        setInputValue("")
      },
    })
  }, [fieldName, registerField, suggestions, selectedId])

  return (
    <Container
      style={containerStyle}
      isErrored={!!error}
      isFilled={isFilled}
      isFocused={isFocused}
    >
      {Icon && <Icon size={20} />}
      <input
        value={inputValue}
        onChange={handleChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        ref={inputRef}
        autoComplete="off"
        {...rest}
      />

      {showSuggestions && filteredSuggestions.length > 0 && (
        <Suggestions>
          {filteredSuggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              onClick={() => selectSuggestion(suggestion)}
              className={index === highlightedIndex ? "highlighted" : ""}
            >
              {suggestion.description}
            </li>
          ))}
        </Suggestions>
      )}

      {error && (
        <Error>
          <FiAlertCircle color="#c53030" size={20} />
        </Error>
      )}
    </Container>
  )
}

export default Autocomplete
